import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Search, Wrench, Zap, Droplets, Hammer, Star, Shield, ArrowRight, Menu, X } from 'lucide-react';
import AuthModal from '../components/AuthModal';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const categories = [
    { name: 'Mechanic', icon: Wrench, color: '#FFB81C' },
    { name: 'Electrician', icon: Zap, color: '#009B4D' },
    { name: 'Plumber', icon: Droplets, color: '#003B5C' },
    { name: 'Carpenter', icon: Hammer, color: '#FFB81C' },
  ];

  const handleSearch = () => {
    navigate(`/workers?search=${searchQuery}`);
  };

  const openAuth = (mode) => {
    setAuthMode(mode);
    setShowAuth(true);
  };

  return (
    <div className="min-h-screen bg-[#F9F7F2]">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <Wrench className="w-8 h-8 text-[#003B5C]" />
              <span className="text-2xl font-bold text-[#003B5C] font-['Outfit']">SkillYaad</span>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-6">
              <button
                data-testid="find-workers-nav-btn"
                onClick={() => navigate('/workers')}
                className="text-[#475569] hover:text-[#003B5C] font-medium transition-colors duration-300"
              >
                Find Workers
              </button>
              {!user ? (
                <>
                  <button
                    data-testid="login-nav-btn"
                    onClick={() => openAuth('login')}
                    className="text-[#475569] hover:text-[#003B5C] font-medium transition-colors duration-300"
                  >
                    Log In
                  </button>
                  <Button
                    data-testid="get-started-nav-btn"
                    onClick={() => openAuth('register')}
                    className="bg-[#003B5C] hover:bg-[#002A42] text-white rounded-full px-6 py-2 font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                  >
                    Get Started
                  </Button>
                </>
              ) : (
                <Button
                  data-testid="dashboard-nav-btn"
                  onClick={() => navigate(
                    user.role === 'worker' ? '/dashboard/worker' : 
                    user.role === 'admin' ? '/admin' : 
                    '/dashboard/customer'
                  )}
                  className="bg-[#003B5C] hover:bg-[#002A42] text-white rounded-full px-6 py-2 font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                >
                  Dashboard
                </Button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-[#003B5C]"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200">
            <div className="px-4 py-4 space-y-3">
              <button
                data-testid="find-workers-mobile-btn"
                onClick={() => {
                  navigate('/workers');
                  setMobileMenuOpen(false);
                }}
                className="block w-full text-left text-[#475569] hover:text-[#003B5C] font-medium py-2"
              >
                Find Workers
              </button>
              {!user ? (
                <>
                  <button
                    data-testid="login-mobile-btn"
                    onClick={() => {
                      openAuth('login');
                      setMobileMenuOpen(false);
                    }}
                    className="block w-full text-left text-[#475569] hover:text-[#003B5C] font-medium py-2"
                  >
                    Log In
                  </button>
                  <Button
                    data-testid="get-started-mobile-btn"
                    onClick={() => {
                      openAuth('register');
                      setMobileMenuOpen(false);
                    }}
                    className="w-full bg-[#003B5C] hover:bg-[#002A42] text-white rounded-full px-6 py-2 font-semibold"
                  >
                    Get Started
                  </Button>
                </>
              ) : (
                <Button
                  data-testid="dashboard-mobile-btn"
                  onClick={() => {
                    navigate(
                      user.role === 'worker' ? '/dashboard/worker' : 
                      user.role === 'admin' ? '/admin' : 
                      '/dashboard/customer'
                    );
                    setMobileMenuOpen(false);
                  }}
                  className="w-full bg-[#003B5C] hover:bg-[#002A42] text-white rounded-full px-6 py-2 font-semibold"
                >
                  Dashboard
                </Button>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section - Tetris Grid */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#003B5C] via-[#003B5C]/90 to-[#009B4D]/20"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-white space-y-8">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight">
                Find Trusted Skilled Workers in <span className="text-[#FFB81C]">Jamaica</span>
              </h1>
              <p className="text-lg sm:text-xl text-gray-200">
                Connect with verified mechanics, plumbers, electricians, and more. Quality service, trusted professionals.
              </p>
              
              {/* Search Bar */}
              <div className="flex flex-col sm:flex-row gap-3 bg-white p-2 rounded-full shadow-2xl" data-testid="hero-search-bar">
                <Input
                  data-testid="hero-search-input"
                  type="text"
                  placeholder="Search for mechanics, plumbers, electricians..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="flex-1 border-none bg-transparent text-[#0F172A] placeholder:text-gray-400 focus-visible:ring-0 focus-visible:ring-offset-0 h-12 px-6"
                />
                <Button
                  data-testid="hero-search-btn"
                  onClick={handleSearch}
                  className="bg-[#FFB81C] hover:bg-[#E5A000] text-[#003B5C] rounded-full px-8 py-3 font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  <Search className="w-5 h-5 mr-2" />
                  Search
                </Button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-6 pt-8">
                <div>
                  <div className="text-3xl font-bold font-['Space_Grotesk'] text-[#FFB81C]">500+</div>
                  <div className="text-sm text-gray-300">Skilled Workers</div>
                </div>
                <div>
                  <div className="text-3xl font-bold font-['Space_Grotesk'] text-[#FFB81C]">1000+</div>
                  <div className="text-sm text-gray-300">Jobs Completed</div>
                </div>
                <div>
                  <div className="text-3xl font-bold font-['Space_Grotesk'] text-[#FFB81C]">4.8</div>
                  <div className="text-sm text-gray-300">Avg Rating</div>
                </div>
              </div>
            </div>

            {/* Right Image Grid */}
            <div className="hidden lg:grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <img
                  src="https://images.pexels.com/photos/3806249/pexels-photo-3806249.jpeg"
                  alt="Mechanic working on engine"
                  className="w-full h-64 object-cover rounded-2xl shadow-2xl"
                />
                <img
                  src="https://images.pexels.com/photos/8853536/pexels-photo-8853536.jpeg"
                  alt="Electrician checking panel"
                  className="w-full h-48 object-cover rounded-2xl shadow-2xl"
                />
              </div>
              <div className="pt-8 space-y-4">
                <img
                  src="https://images.pexels.com/photos/6474205/pexels-photo-6474205.jpeg"
                  alt="Plumber fixing sink"
                  className="w-full h-48 object-cover rounded-2xl shadow-2xl"
                />
                <img
                  src="https://images.pexels.com/photos/7415122/pexels-photo-7415122.jpeg"
                  alt="Happy customer"
                  className="w-full h-64 object-cover rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-[#003B5C] mb-4">
            Browse by Category
          </h2>
          <p className="text-center text-[#475569] mb-12">Find the right professional for your needs</p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {categories.map((category, idx) => {
              const Icon = category.icon;
              return (
                <button
                  key={idx}
                  data-testid={`category-${category.name.toLowerCase()}-btn`}
                  onClick={() => navigate(`/workers?category=${category.name}`)}
                  className="group bg-[#F9F7F2] rounded-2xl p-8 border border-transparent hover:border-[#FFB81C]/20 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                >
                  <div className="flex flex-col items-center text-center space-y-4">
                    <div className="w-16 h-16 rounded-full bg-white shadow-md flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <Icon className="w-8 h-8" style={{ color: category.color }} />
                    </div>
                    <h3 className="font-semibold text-[#0F172A]">{category.name}</h3>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-[#F9F7F2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-[#003B5C] mb-4">
            How It Works
          </h2>
          <p className="text-center text-[#475569] mb-16">Get the help you need in three simple steps</p>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-[#003B5C] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto shadow-lg">
                1
              </div>
              <h3 className="text-xl font-bold text-[#0F172A]">Search & Browse</h3>
              <p className="text-[#475569]">Find skilled workers by category, location, and ratings</p>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-[#FFB81C] text-[#003B5C] rounded-full flex items-center justify-center text-2xl font-bold mx-auto shadow-lg">
                2
              </div>
              <h3 className="text-xl font-bold text-[#0F172A]">Review & Choose</h3>
              <p className="text-[#475569]">Check ratings, reviews, and profiles to find the perfect match</p>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-[#009B4D] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto shadow-lg">
                3
              </div>
              <h3 className="text-xl font-bold text-[#0F172A]">Book & Connect</h3>
              <p className="text-[#475569]">Send a job request and get your work done by professionals</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#003B5C] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
          <Shield className="w-16 h-16 text-[#FFB81C] mx-auto" />
          <h2 className="text-3xl sm:text-4xl font-bold">
            Are You a Skilled Worker?
          </h2>
          <p className="text-lg text-gray-200">
            Join SkillYaad and connect with customers looking for your expertise. Build your reputation and grow your business.
          </p>
          <Button
            data-testid="join-as-worker-btn"
            onClick={() => openAuth('register')}
            className="bg-[#FFB81C] hover:bg-[#E5A000] text-[#003B5C] rounded-full px-8 py-4 text-lg font-bold transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-1"
          >
            Join as a Worker
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-12 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex items-center space-x-2">
              <Wrench className="w-6 h-6 text-[#003B5C]" />
              <span className="text-xl font-bold text-[#003B5C]">SkillYaad</span>
            </div>
            <p className="text-[#475569] text-sm">
              © 2025 SkillYaad. Connecting Jamaica with skilled professionals.
            </p>
          </div>
        </div>
      </footer>

      {showAuth && (
        <AuthModal
          isOpen={showAuth}
          onClose={() => setShowAuth(false)}
          mode={authMode}
          setMode={setAuthMode}
        />
      )}
    </div>
  );
};

export default LandingPage;